import React, { useState, useEffect } from 'react';
import { FileText, Search, Filter, Edit, Send, Trash2, Eye, CheckCircle, Clock, XCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { getAllSunMedInvoices, deleteSunMedInvoice, resendSunMedInvoice } from '../utils/api';

const SunMedInvoiceList = ({ onCreateNew, onEdit, onView, onSend }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showResendModal, setShowResendModal] = useState(false);
  const [invoiceToResend, setInvoiceToResend] = useState(null);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const data = await getAllSunMedInvoices();
      setInvoices(data);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
      alert(`Failed to load invoices: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!invoiceToDelete) return;
    
    try {
      setDeleting(true);
      await deleteSunMedInvoice(invoiceToDelete.id);
      alert('Invoice deleted successfully');
      setShowDeleteModal(false);
      setInvoiceToDelete(null);
      fetchInvoices();
    } catch (error) {
      console.error('Failed to delete invoice:', error);
      alert(`Failed to delete invoice: ${error.response?.data?.detail || error.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const handleResend = async () => {
    if (!invoiceToResend) return;

    try {
      setResending(true);
      const result = await resendSunMedInvoice(invoiceToResend.id);
      const recipient = result?.recipient || invoiceToResend.recipientEmail || invoiceToResend.billToEmail || 'recipient';
      const copyTo = result?.copySentTo ? ` (CC: ${result.copySentTo})` : '';
      alert(`Invoice resent successfully to ${recipient}${copyTo}`);
      setShowResendModal(false);
      setInvoiceToResend(null);
      fetchInvoices();
    } catch (error) {
      console.error('Failed to resend invoice:', error);
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (error.message || 'Unknown error');
      alert(`Failed to resend invoice: ${msg}`);
    } finally {
      setResending(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent':
        return 'bg-green-500/10 text-green-400 border-green-500/30';
      case 'ready':
        return 'bg-blue-500/10 text-blue-400 border-blue-500/30';
      case 'draft':
        return 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30';
      case 'failed':
        return 'bg-red-500/10 text-red-400 border-red-500/30';
      default:
        return 'bg-gray-500/10 text-gray-400 border-gray-500/30';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
        return <CheckCircle className="w-3.5 h-3.5" />;
      case 'ready':
        return <AlertCircle className="w-3.5 h-3.5" />;
      case 'draft':
        return <Clock className="w-3.5 h-3.5" />;
      case 'failed':
        return <XCircle className="w-3.5 h-3.5" />;
      default:
        return <Clock className="w-3.5 h-3.5" />;
    }
  };

  const filteredInvoices = invoices.filter((invoice) => {
    const matchesSearch =
      invoice.invoiceNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.patientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.billToEmail?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center space-x-2">
            <FileText className="w-6 h-6 text-cyan-400" />
            <span>Sun Med Invoices</span>
          </h2>
          <p className="text-gray-400 text-sm mt-1">Manage medical transport invoicing for Sun Med</p>
        </div>
        <button
          onClick={onCreateNew}
          className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg font-bold hover:from-cyan-600 hover:to-blue-600 transition flex items-center space-x-2"
        >
          <FileText className="w-5 h-5" />
          <span>Create New Invoice</span>
        </button>
      </div>

      {/* Search and Filter */}
      <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm p-6 rounded-2xl border border-cyan-500/20">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by invoice number, patient name, or email..."
              className="w-full pl-12 pr-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
            />
          </div>

          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="pl-12 pr-8 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300 appearance-none"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="ready">Ready</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Invoice List */}
      {loading ? (
        <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm p-12 rounded-2xl border border-cyan-500/20 text-center">
          <p className="text-gray-400 text-lg">Loading invoices...</p>
        </div>
      ) : filteredInvoices.length === 0 ? (
        <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm p-12 rounded-2xl border border-cyan-500/20 text-center">
          <FileText className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 text-lg">No invoices found</p>
          <p className="text-gray-500 text-sm mt-2">
            {searchTerm || statusFilter !== 'all'
              ? 'Try adjusting your search or filters'
              : 'Create your first Sun Med invoice to get started'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredInvoices.map((invoice) => (
            <div
              key={invoice.id}
              className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm p-6 rounded-2xl border border-cyan-500/20 hover:border-cyan-500/50 transition-all duration-300"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold text-white mb-1">
                    {invoice.invoiceNumber}
                  </h3>
                  <p className="text-gray-400 text-sm">Patient: {invoice.patientName}</p>
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold border flex items-center space-x-1 ${getStatusColor(
                    invoice.status
                  )}`}
                >
                  {getStatusIcon(invoice.status)}
                  <span className="capitalize">{invoice.status}</span>
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <p className="text-gray-400 text-sm mb-1">Recipient Email</p>
                  <p className="text-white text-sm truncate">{invoice.billToEmail}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">Total Amount</p>
                  <p className="text-white text-sm font-semibold">${invoice.total.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">Last Updated</p>
                  <p className="text-white text-sm">{formatDate(invoice.updatedAt)}</p>
                </div>
              </div>

              {invoice.sentAt && (
                <div className="mb-4 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <p className="text-green-400 text-xs">
                    <CheckCircle className="w-3.5 h-3.5 inline mr-1" />
                    Sent on {formatDate(invoice.sentAt)} to {invoice.recipientEmail}
                  </p>
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-4 border-t border-cyan-500/20">
                <button
                  onClick={() => onView(invoice)}
                  className="px-4 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 border border-blue-500/30 transition text-sm font-semibold flex items-center space-x-2"
                >
                  <Eye className="w-4 h-4" />
                  <span>View</span>
                </button>

                {(invoice.status === 'draft' || invoice.status === 'ready') && (
                  <>
                    <button
                      onClick={() => onEdit(invoice)}
                      className="px-4 py-2 bg-yellow-500/20 text-yellow-400 rounded-lg hover:bg-yellow-500/30 border border-yellow-500/30 transition text-sm font-semibold flex items-center space-x-2"
                    >
                      <Edit className="w-4 h-4" />
                      <span>Edit</span>
                    </button>

                    <button
                      onClick={() => onSend(invoice)}
                      className="px-4 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 border border-green-500/30 transition text-sm font-semibold flex items-center space-x-2"
                    >
                      <Send className="w-4 h-4" />
                      <span>Send</span>
                    </button>
                  </>
                )}

                {invoice.status === 'draft' && (
                  <button
                    onClick={() => {
                      setInvoiceToDelete(invoice);
                      setShowDeleteModal(true);
                    }}
                    className="px-4 py-2 bg-red-600/20 text-red-400 rounded-lg hover:bg-red-600/30 border border-red-600/30 transition text-sm font-semibold flex items-center space-x-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Delete</span>
                  </button>
                )}

                {invoice.status === 'sent' && (
                  <button
                    data-testid={`sunmed-resend-btn-${invoice.invoiceNumber}`}
                    onClick={() => {
                      setInvoiceToResend(invoice);
                      setShowResendModal(true);
                    }}
                    className="px-4 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg hover:bg-cyan-500/30 border border-cyan-500/30 transition text-sm font-semibold flex items-center space-x-2"
                    title="Resend this invoice to the recipient (admin is auto-CC'd)"
                  >
                    <RefreshCw className="w-4 h-4" />
                    <span>Resend</span>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && invoiceToDelete && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 max-w-md w-full border-2 border-red-500/30">
            <h3 className="text-2xl font-bold mb-4 text-red-400">Delete Invoice</h3>
            <p className="text-gray-300 mb-2">
              Are you sure you want to delete this invoice?
            </p>
            <div className="bg-gray-800/50 p-4 rounded-lg mb-6 border border-red-500/20">
              <p className="text-sm text-gray-300">
                <strong>Invoice:</strong> {invoiceToDelete.invoiceNumber}
              </p>
              <p className="text-sm text-gray-300">
                <strong>Patient:</strong> {invoiceToDelete.patientName}
              </p>
              <p className="text-sm text-gray-300">
                <strong>Total:</strong> ${invoiceToDelete.total.toFixed(2)}
              </p>
            </div>
            <p className="text-sm text-red-400 mb-6 font-semibold">
              ⚠️ This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setInvoiceToDelete(null);
                }}
                className="flex-1 px-6 py-3 bg-gray-700 text-white rounded-lg font-bold hover:bg-gray-600 transition"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                {deleting ? 'Deleting...' : 'Delete Invoice'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resend Confirmation Modal */}
      {showResendModal && invoiceToResend && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 max-w-md w-full border-2 border-cyan-500/30">
            <h3 className="text-2xl font-bold mb-4 text-cyan-400">Resend Invoice</h3>
            <p className="text-gray-300 mb-2">
              Resend this invoice email to the recipient? A copy will be CC'd to the admin inbox.
            </p>
            <div className="bg-gray-800/50 p-4 rounded-lg mb-6 border border-cyan-500/20">
              <p className="text-sm text-gray-300">
                <strong>Invoice:</strong> {invoiceToResend.invoiceNumber}
              </p>
              <p className="text-sm text-gray-300">
                <strong>Patient:</strong> {invoiceToResend.patientName}
              </p>
              <p className="text-sm text-gray-300">
                <strong>Recipient:</strong> {invoiceToResend.recipientEmail || invoiceToResend.billToEmail}
              </p>
              <p className="text-sm text-gray-300">
                <strong>Total:</strong> ${invoiceToResend.total.toFixed(2)}
              </p>
            </div>
            <div className="flex gap-3">
              <button
                data-testid="sunmed-resend-cancel"
                onClick={() => {
                  setShowResendModal(false);
                  setInvoiceToResend(null);
                }}
                className="flex-1 px-6 py-3 bg-gray-700 text-white rounded-lg font-bold hover:bg-gray-600 transition"
                disabled={resending}
              >
                Cancel
              </button>
              <button
                data-testid="sunmed-resend-confirm"
                onClick={handleResend}
                disabled={resending}
                className="flex-1 px-6 py-3 bg-cyan-600 text-white rounded-lg font-bold hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                {resending ? 'Resending...' : 'Confirm Resend'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SunMedInvoiceList;
